import React from "react";
import { useSelector } from "react-redux";
import { Modal, Box, Typography } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import { makeStyles } from "@material-ui/core/styles";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import SupervisorAccountIcon from "@material-ui/icons/SupervisorAccount";
import SecurityIcon from "@material-ui/icons/Security";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CloseIcon from "@material-ui/icons/Close";
import HighlightOffOutlinedIcon from "@material-ui/icons/HighlightOffOutlined";
import jwt from "jwt-decode";
import { useDispatch } from "react-redux";
import { setUserProfileDialog } from "../../js/reducers/userProfileDialog";
import { UserProfile as UserProfileActions } from "../../js/actions";
import { OAuth as OAuthActions } from "../../js/actions";
import { setAlertDialog } from "../../js/reducers/alertDialog";
import { setSnackbar } from "../../js/reducers/snackbar";
import { useHistory } from "react-router-dom";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import TokenStorage from "../../js/utils/tokenStorage";

const useStyles = makeStyles((theme) => ({
  "@keyframes cardPop": {
    "0%": { opacity: 0, transform: "translateY(12px) scale(0.98)" },
    "100%": { opacity: 1, transform: "translateY(0) scale(1)" },
  },
  overlay: {
    position: "fixed",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backdropFilter: "blur(4px)",
    backgroundColor: "rgba(15,23,42,0.35)",
    zIndex: 999,
    padding: "1.5rem",
  },
  card: {
    width: "100%",
    maxWidth: "620px",
    backgroundColor: "#fff",
    borderRadius: "16px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08), 0 12px 40px rgba(0,0,0,0.12)",
    overflow: "hidden",
    fontFamily: "Bosch Sans Regular",
    animation: "$cardPop 0.25s cubic-bezier(0.16, 1, 0.3, 1)",
  },
  /* ── Banner ── */
  banner: {
    background: "#F8FAFC",
    borderBottom: "1px solid #e2e8f0",
    padding: "20px 20px",
    position: "relative",
    textAlign: "center",
  },
  closeIconBtn: {
    position: "absolute",
    top: "12px",
    right: "12px",
    background: "rgba(15,23,42,0.06)",
    border: "none",
    cursor: "pointer",
    color: "#0f172a",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "5px",
    borderRadius: "8px",
    transition: "all 0.15s",
    "&:hover": { background: "rgba(15,23,42,0.12)" },
  },
  avatarRing: {
    width: "72px",
    height: "72px",
    borderRadius: "50%",
    background: "linear-gradient(135deg, #1a56db 0%, #1e40af 100%)",
    border: "3px solid #dbeafe",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 10px",
    boxShadow: "0 4px 12px rgba(26,86,219,0.25)",
  },
  bannerName: {
    color: "#0f172a",
    fontWeight: 700,
    fontSize: "16px !important",
    margin: "0 0 6px",
    letterSpacing: "0.02em",
  },
  rolePill: {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    backgroundColor: "#dbeafe",
    border: "1px solid rgba(26,86,219,0.25)",
    borderRadius: "20px",
    padding: "3px 12px",
  },
  roleText: {
    color: "#1a56db",
    fontSize: "11px !important",
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
  },
  /* ── Body ── */
  body: {
    padding: "0 20px 20px",
    marginTop: "-1px",
  },
  sectionLabel: {
    fontSize: "10px !important",
    fontWeight: 700,
    color: "#94a3b8",
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    marginBottom: "8px",
    marginTop: "16px",
  },
  modulesWrap: {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
  },
  moduleChip: {
    fontSize: "11px !important",
    fontWeight: 600,
    color: "#1a56db",
    backgroundColor: "#dbeafe",
    border: "1px solid #bfdbfe",
    borderRadius: "20px",
    padding: "3px 10px",
    lineHeight: 1.5,
    letterSpacing: "0.05em",
    transition: "all 0.15s",
    "&:hover": { backgroundColor: "#bfdbfe", transform: "translateY(-1px)" },
  },
  noModulesText: {
    fontSize: "12px !important",
    color: "#94a3b8",
  },
  /* ── Revoke box ── */
  revokeBox: {
    backgroundColor: "#fef2f2",
    border: "1px solid #fecaca",
    borderRadius: "10px",
    padding: "12px 14px",
    marginTop: "8px",
  },
  revokeBoxHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
  },
  revokeBoxTitle: {
    fontSize: "14px !important",
    fontWeight: 600,
    color: "#b91c1c",
    margin: 0,
  },
  revokeDesc: {
    fontSize: "13px !important",
    color: "#ef4444",
    lineHeight: 1.5
  },
  revokeBtn: {
    fontSize: "13px !important",
    fontWeight: 600,
    padding: "5px 14px",
    borderRadius: "6px",
    border: "1px solid #fca5a5",
    backgroundColor: "#fff",
    color: "#ef4444",
    cursor: "pointer",
    whiteSpace: "nowrap",
    transition: "all 0.15s",
    "&:hover": {
      backgroundColor: "#ef4444",
      borderColor: "#ef4444",
      color: "#fff",
      boxShadow: "0 2px 8px rgba(239,68,68,0.3)",
    },
  },
  revokedRow: {
    display: "flex",
    alignItems: "center",
    gap: "7px",
    marginTop: "4px",
  },
  revokedText: {
    fontSize: "12px !important",
    color: "#059669",
    fontWeight: 500,
  },
  checkCircleIcon: {
    fontSize: "16px !important",
    color: "#10b981",
  },
  /* ── Footer ── */
  footer: {
    padding: "14px 20px",
    borderTop: "1px solid #e2e8f0",
    display: "flex",
    justifyContent: "flex-end",
  },
  logoutBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "7px",
    background: "linear-gradient(135deg, #1a56db 0%, #1e40af 100%)",
    color: "#fff",
    fontSize: "13px !important",
    fontWeight: 600,
    padding: "9px 20px",
    borderRadius: "8px",
    border: "none",
    cursor: "pointer",
    letterSpacing: "0.02em",
    boxShadow: "0 2px 8px rgba(26,86,219,0.35)",
    transition: "all 0.15s",
    "&:hover": {
      boxShadow: "0 4px 14px rgba(26,86,219,0.45)",
      transform: "translateY(-1px)",
    },
  },
  supervisorIcon: { fontSize: "36px !important", color: "#fff" },
  securityIcon: { fontSize: "11px !important", color: "#1a56db" },
  closeIconSize: { fontSize: "17px !important" },
  exitIcon: { fontSize: "16px !important" },
  dialogPaper: {
    borderRadius: "15px !important",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06), 0 4px 20px rgba(0,0,0,0.05) !important",
  },
  dialogRoot: {
    zIndex: "1400 !important",
  },
  dialogHeaderIcon: {
    fontSize: "22px !important",
    color: "#dc2626",
  },
  /* ── Revoke dialog ── */
  dialogContentMain: {
    fontWeight: 500,
    fontSize: "14px !important",
    color: "#0f172a",
    lineHeight: 1.6,
  },
  dialogContentSub: {
    fontWeight: 400,
    fontSize: "12px !important",
    color: "#64748b",
    lineHeight: 1.5,
  },
  dialogHeader: {
    padding: "14px 20px",
    borderBottom: "1px solid #f1f5f9",
    display: "flex",
    alignItems: "center",
    gap: 10,
    backgroundColor: "#FFF5F5",
  },
  dialogHeaderIconBox: {
    width: 40,
    height: 40,
    borderRadius: 8,
    backgroundColor: "#fee2e2",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  dialogHeaderTitle: {
    fontWeight: 700,
    fontSize: "16px !important",
    color: "#991b1b",
    textTransform: "uppercase",
    letterSpacing: 0.2,
  },
  dialogBody: {
    padding: "18px 20px",
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  dialogFooter: {
    padding: "12px 20px",
    borderTop: "1px solid #e2e8f0",
  },
  dialogBtnOutlined: {
    borderRadius: 8,
    fontWeight: 600,
    color: "#0f172a",
    borderColor: "#e2e8f0",
    textTransform: "none",
    padding: "8px 20px",
    fontSize: "13px !important",
  },
  dialogBtnConfirm: {
    borderRadius: 8,
    fontWeight: 600,
    background: "linear-gradient(135deg, #dc2626 0%, #991b1b 100%)",
    textTransform: "none",
    padding: "8px 20px",
    fontSize: "13px !important",
    boxShadow: "0 2px 8px rgba(220, 38, 38, 0.35)",
  },
}));

const UserProfile = (props) => {
  const history = useHistory();
  const classes = useStyles();
  const dispatch = useDispatch();

  const [open, setOpen] = React.useState(false);
  const [revoked, setRevoked] = React.useState(false);
  const [openRevokeDialog, setOpenRevokeDialog] = React.useState(false);

  useSelector((state) => {
    if (open == false)
      if (state?.userProfileDialog?.userProfileOpen) {
        setOpen(state?.userProfileDialog?.userProfileOpen);
      }
  });

  const uid = useSelector((state) => state.OAuth.loginUserData.uid || {});
  const token = useSelector((state) => {
    let user = "";
    if (state?.OAuth?.authData?.access_token !== undefined)
      user = jwt(state?.OAuth?.authData?.access_token) || "";
    return user;
  });

  const loginUserDetails = useSelector((state) => {
    let authData = state?.OAuth?.authData?.user_name || "";
    if (
      state?.OAuth?.loginUserData?.firstName !== undefined ||
      state?.OAuth?.loginUserData?.firstName !== ""
    )
      return (
        state?.OAuth?.loginUserData?.firstName +
        " " +
        state?.OAuth?.loginUserData?.lastName || ""
      );
    else return authData;
  });

  const modules = token?.authorities
    ? token.authorities
      .toString()
      .replace(/ROLE_/g, "")
      .split(",")
      .map((mod) => mod.trim().replace(/_/g, " "))
      .filter((mod) => mod.length > 0)
    : [];

  const handleClose = () => {
    setOpen(false);
    dispatch(setUserProfileDialog(false));
  };

  const logout = () => {
    handleClose();
    localStorage.removeItem("token_name");
    dispatch(UserProfileActions.clearLoginUserData()).then(function () {
      TokenStorage.clearAllCookies();
      history.push(window._appConstants.useCommonDefaults.navRef.login);
    });
  };

  const handleRevoke = () => {
    setOpenRevokeDialog(true);
  };

  const handleRevokeConfirm = () => {
    setOpenRevokeDialog(false);
    handleClose();
    localStorage.removeItem("token_name");
    dispatch(
      OAuthActions.updateUsersPolicy(uid, {
        hasAcceptedTnc: false,
        hasAcceptedPrivacyPolicy: false,
      })
    ).then(function (userDetails) {
      if (userDetails["error"] === undefined) {
        dispatch(setSnackbar(true, "success", "Update Successful"));
        setRevoked(true);
        dispatch(UserProfileActions.clearLoginUserData()).then(function () {
          history.push(window._appConstants.useCommonDefaults.navRef.login);
        });
      } else {
        dispatch(
          setAlertDialog(
            true,
            null,
            "error",
            "Update User",
            "Update unsuccessful.\nDetails Of Failure: " + userDetails.message,
            "OK",
            ""
          )
        );
      }
    });
  };

  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <div className={classes.overlay} onClick={handleClose}>
          <div className={classes.card} onClick={(e) => e.stopPropagation()}>

            {/* Dark banner header */}
            <div className={classes.banner}>
              <button onClick={handleClose} className={classes.closeIconBtn}>
                <CloseIcon className={classes.closeIconSize} />
              </button>
              <div className={classes.avatarRing}>
                <SupervisorAccountIcon className={classes.supervisorIcon} />
              </div>
              <p className={classes.bannerName}>{loginUserDetails.toUpperCase()}</p>
              <div className={classes.rolePill}>
                <SecurityIcon className={classes.securityIcon} />
                <span className={classes.roleText}>
                  {token?.role?.replace(/_/g, " ") || "User"}
                </span>
              </div>
            </div>

            {/* Body */}
            <div className={classes.body}>
              {/* Modules */}
              <p className={classes.sectionLabel}>Authorized Modules</p>
              <div className={classes.modulesWrap}>
                {modules.length > 0 ? (
                  modules.map((mod, idx) => (
                    <span key={idx} className={classes.moduleChip}>{mod}</span>
                  ))
                ) : (
                  <span className={classes.noModulesText}>No modules assigned</span>
                )}
              </div>

              {/* Revoke */}
              <p className={classes.sectionLabel}>Terms &amp; Privacy Policy</p>
              <div className={classes.revokeBox}>
                {revoked ? (
                  <div className={classes.revokedRow}>
                    <CheckCircleIcon className={classes.checkCircleIcon} />
                    <span className={classes.revokedText}>Terms successfully revoked.</span>
                  </div>
                ) : (
                  <>
                    <p className={classes.revokeDesc}>
                      By revoking the terms of use and privacy policy you will no longer be able to explore and use the application.
                    </p>
                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                      <button onClick={handleRevoke} className={classes.revokeBtn}>
                        Revoke
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className={classes.footer}>
              <button onClick={logout} className={classes.logoutBtn}>
                <ExitToAppIcon className={classes.exitIcon} />
                Log Out
              </button>
            </div>

          </div>
        </div>
      </Modal>

      <Dialog
        open={openRevokeDialog}
        keepMounted
        maxWidth="sm"
        fullWidth
        classes={{ root: classes.dialogRoot, paper: classes.dialogPaper }}
      >
        <Box className={classes.dialogHeader}>
          <Box className={classes.dialogHeaderIconBox}>
            <HighlightOffOutlinedIcon className={classes.dialogHeaderIcon} />
          </Box>
          <Typography className={classes.dialogHeaderTitle}>
            Confirm Revoke Privileges
          </Typography>
        </Box>

        <DialogContent className={classes.dialogBody}>
          <Typography className={classes.dialogContentMain}>
            Are you sure you want to revoke the privacy policy &amp; terms and conditions?
          </Typography>
          <Typography className={classes.dialogContentSub}>
            You will be logged out and will not be able to use the application until the terms are accepted again. This action cannot be undone.
          </Typography>
        </DialogContent>

        <DialogActions className={classes.dialogFooter}>
          <Button
            onClick={() => setOpenRevokeDialog(false)}
            variant="outlined"
            className={classes.dialogBtnOutlined}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRevokeConfirm}
            className={classes.dialogBtnConfirm}
          >
            Revoke
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default React.memo(UserProfile);
